import React, { useState, useMemo, useRef } from 'react';
import {
  X, Camera, FileText, Paperclip, Loader, CheckCircle,
  ShieldCheck, Lock, AlertTriangle, Calendar, ChevronDown, ChevronUp, Wand2,
} from 'lucide-react';
import { uprightJpegBytes } from '../utils/imageOrient';

// ── Fleet-wide insurance policy ───────────────────────────────────────────────
// One comprehensive policy covers many vehicles. Upload the policy/schedule ONCE,
// Claude reads the policy expiry date + the schedule of covered regos, and we bulk-
// set Insurance Expiry on every matched vehicle (that's the date the Fleet table &
// compliance alerts key off). Optionally files the same PDF under each matched
// vehicle's private documents as "Insurance". Admin-only.
//
// Robustness: insurer schedules sometimes misprint a plate (e.g. ETY80L for the
// real ETU80L). Unmatched policy rows get a fuzzy "did you mean…" suggestion and a
// manual remap dropdown so the cover still lands on the right vehicle. A read-only
// panel also lists COMPANY vehicles in the fleet that aren't on the uploaded policy
// (owner-drivers self-insure, so they're filtered out as noise).

const FLEET_URL = '/.netlify/functions/fleet-sb';
const VEHICLE_ADMIN_URL = '/.netlify/functions/vehicle-admin-sb';
const MAX_DOC_MB = 4; // base64 inflates ~33%; Netlify function body limit is 6 MB

const toLocalISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => toLocalISO(new Date());
const canonRego = (s) => (s || '').toUpperCase().replace(/[\s-]/g, '');
const isOwnerDriver = (owner) => /owner\s*driver/i.test(owner || '');

// Levenshtein distance — drives the "did you mean…" suggestion for a misprinted plate.
function lev(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = () => reject(new Error('Could not read file'));
  reader.readAsDataURL(file);
});

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const vehLabel = (v) => [v.year, v.make, v.model].filter(Boolean).join(' ').trim();

const POLICY_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    policyExpiry:  { type: 'string', description: 'Policy expiry / end of the period of insurance as YYYY-MM-DD. Empty string if not shown.' },
    insurer:       { type: 'string', description: 'Insurer / underwriter name, else empty string.' },
    policyNumber:  { type: 'string', description: 'Policy number, else empty string.' },
    vehicles: {
      type: 'array',
      description: 'Every vehicle listed on the policy schedule of insured vehicles.',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          rego:        { type: 'string', description: 'Registration / plate, uppercase no spaces. Empty string if a listed item shows no plate.' },
          description: { type: 'string', description: 'Make / model / year or any vehicle description shown, else empty string.' },
        },
        required: ['rego', 'description'],
      },
    },
    summary: { type: 'string', description: 'One short sentence describing the document.' },
  },
  required: ['policyExpiry', 'insurer', 'policyNumber', 'vehicles', 'summary'],
};

async function extractPolicyFields(file) {
  const keyResp = await fetch('/.netlify/functions/get-claude-key');
  const { key } = await keyResp.json();
  if (!key) throw new Error('Could not get API key');

  const data = await fileToBase64(file);
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  const docBlock = isPdf
    ? { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data } }
    : { type: 'image', source: { type: 'base64', media_type: file.type || 'image/jpeg', data } };

  const resp = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': key,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-opus-4-8',
      max_tokens: 4000,
      output_config: { format: { type: 'json_schema', schema: POLICY_SCHEMA } },
      messages: [{
        role: 'user',
        content: [
          docBlock,
          { type: 'text', text: `Today's date is ${todayISO()}. This is a fleet motor insurance document — a policy schedule, tax invoice, or certificate of currency. Extract the policy expiry date (the end of the period of insurance) and the FULL list of vehicles on the insured-vehicle schedule, each with its registration plate exactly as printed. Include every vehicle row you can see. Dates without a year belong to the current or next year as appropriate for an insurance period, never the distant past. All amounts are AUD.` },
        ],
      }],
    }),
  });
  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`Claude error ${resp.status}: ${errText.substring(0, 150)}`);
  }
  const result = await resp.json();
  const raw = (result.content || []).filter(b => b.type === 'text').map(b => b.text).join('');
  return JSON.parse(raw);
}

const labelStyle = {
  fontSize: '.7rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5,
};
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)' };

export default function FleetInsuranceModal({ vehicles, setVehicles, currentUser, onClose, addNotification }) {
  const [pdfFile, setPdfFile] = useState(null);
  const [scanPages, setScanPages] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [policy, setPolicy] = useState(null);       // raw OCR result
  const [expiry, setExpiry] = useState('');         // editable applied date
  const [remap, setRemap] = useState({});           // policy-rego canon → fleet-rego canon (manual fix)
  const [fileDocs, setFileDocs] = useState(true);   // also file PDF under each vehicle
  const [showGaps, setShowGaps] = useState(false);  // FYI panel: company vehicles not on this policy
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(null);   // { done, total, current }
  const [doneSummary, setDoneSummary] = useState(null);
  const fileInputRef = useRef(null);

  const liveVehicles = useMemo(() => vehicles.filter(v => v.rego && !v.archived), [vehicles]);
  const fleetByCanon = useMemo(() => new Map(liveVehicles.map(v => [canonRego(v.rego), v])), [liveVehicles]);
  const fleetSorted  = useMemo(() => [...liveVehicles].sort((a, b) => (a.rego > b.rego ? 1 : -1)), [liveVehicles]);

  // Match the OCR'd schedule regos against the fleet, honouring any manual remaps.
  const matches = useMemo(() => {
    if (!policy) return [];
    return (policy.vehicles || [])
      .filter(row => canonRego(row.rego))
      .map(row => {
        const pc = canonRego(row.rego);
        let vehicle = fleetByCanon.get(pc) || null;
        let remapped = false;
        if (!vehicle && remap[pc]) {
          vehicle = fleetByCanon.get(remap[pc]) || null;
          remapped = !!vehicle;
        }
        return { rego: row.rego, canon: pc, description: row.description, vehicle, remapped };
      });
  }, [policy, fleetByCanon, remap]);

  const matched   = matches.filter(m => m.vehicle);
  const unmatched = matches.filter(m => !m.vehicle);

  // Plates already claimed by an exact match — excluded from suggestions/remap targets.
  const claimedCanon = useMemo(() => {
    const s = new Set(matched.map(m => canonRego(m.vehicle.rego)));
    return s;
  }, [matched]);

  const suggestFor = (policyCanon) => {
    let best = null, bestD = 99;
    for (const v of liveVehicles) {
      const c = canonRego(v.rego);
      if (!c || claimedCanon.has(c)) continue;
      const d = lev(policyCanon, c);
      if (d < bestD) { bestD = d; best = v; }
    }
    return bestD <= 2 ? best : null;
  };

  // Company vehicles in the fleet that AREN'T on this policy (owner-drivers self-insure → hidden).
  const notOnPolicy = useMemo(() => {
    if (!policy) return [];
    const onPolicy = new Set(matches.map(m => m.canon));
    const covered  = new Set(matched.map(m => canonRego(m.vehicle.rego))); // incl. remapped targets
    return liveVehicles
      .filter(v => !isOwnerDriver(v.owner))
      .filter(v => !onPolicy.has(canonRego(v.rego)) && !covered.has(canonRego(v.rego)))
      .sort((a, b) => (a.owner || '').localeCompare(b.owner || '') || (a.rego > b.rego ? 1 : -1));
  }, [policy, matches, matched, liveVehicles]);

  const setRemapFor = (policyCanon, fleetCanon) =>
    setRemap(prev => {
      const next = { ...prev };
      if (fleetCanon) next[policyCanon] = fleetCanon; else delete next[policyCanon];
      return next;
    });

  const runExtract = async (f) => {
    setExtracting(true);
    setPolicy(null);
    setRemap({});
    setDoneSummary(null);
    try {
      const x = await extractPolicyFields(f);
      setPolicy(x);
      setExpiry(x.policyExpiry || '');
      if (!(x.vehicles || []).length) {
        addNotification('No vehicle schedule found in the document — check it lists the regos, or set the date and pick vehicles manually', 'error');
      }
    } catch (e) {
      setPolicy(null);
      addNotification(`Couldn't read the policy (${e.message}). If it's password-protected, open it in Preview then File → Export to PDF to save an unlocked copy and try again.`, 'error');
    } finally {
      setExtracting(false);
    }
  };

  const acceptFile = (f) => {
    if (!f) return;
    const okType = f.type === 'application/pdf' || f.type.startsWith('image/') || /\.pdf$/i.test(f.name);
    if (!okType) { addNotification('Only PDF or image files can be read', 'error'); return; }
    if (f.size > MAX_DOC_MB * 1024 * 1024) { addNotification(`File too large — max ${MAX_DOC_MB} MB`, 'error'); return; }
    setScanPages([]);
    setPdfFile(f);
    runExtract(f);
  };

  const handleFilePick = (e) => { acceptFile(e.target.files?.[0]); e.target.value = ''; };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer?.files?.[0]); };

  // Multi-page camera scan → one combined PDF (pdf-lib, lazy), same as the docs drawer.
  const addScanPage = async (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { addNotification('That wasn’t a photo — use the camera', 'error'); return; }
    if (f.size > MAX_DOC_MB * 1024 * 1024) { addNotification(`Page too large — max ${MAX_DOC_MB} MB`, 'error'); return; }
    const pages = [...scanPages, f];
    setScanPages(pages);
    try {
      const { PDFDocument } = await import('pdf-lib');
      const pdf = await PDFDocument.create();
      for (const img of pages) {
        const upright = await uprightJpegBytes(img);
        let embedded;
        if (upright) {
          embedded = await pdf.embedJpg(upright);
        } else {
          const buf = await img.arrayBuffer();
          embedded = img.type === 'image/png' ? await pdf.embedPng(buf) : await pdf.embedJpg(buf);
        }
        const page = pdf.addPage([embedded.width, embedded.height]);
        page.drawImage(embedded, { x: 0, y: 0, width: embedded.width, height: embedded.height });
      }
      const bytes = await pdf.save();
      const built = new File([bytes], `fleet-insurance-${pages.length}p.pdf`, { type: 'application/pdf' });
      setPdfFile(built);
      runExtract(built); // re-read the whole multi-page schedule each time
    } catch (e) {
      addNotification('Could not build the PDF from that photo — ' + e.message, 'error');
    }
  };
  const handleScanPick = (e) => { addScanPage(e.target.files?.[0]); e.target.value = ''; };

  const clearFile = () => {
    setPdfFile(null); setScanPages([]); setPolicy(null); setExpiry(''); setRemap({}); setDoneSummary(null);
  };

  // Push insurance_exp onto a vehicle without clobbering its other fields.
  const updateVehicleInsurance = (v, isoDate) =>
    fetch(FLEET_URL, {
      method: 'POST',
      body: JSON.stringify({
        action: 'updateFleet',
        rego:          v.rego,
        login:         v.login         || '',
        make:          v.make          || '',
        model:         v.model         || '',
        year:          v.year          || '',
        rego_exp:      v.rego_exp      || '',
        insurance_exp: isoDate,
        service_due:   v.service_due   || '',
        owner:         v.owner         || '',
      }),
    }).then(r => r.json());

  const handleApply = async () => {
    if (!expiry) { addNotification('Set the policy expiry date first', 'error'); return; }
    if (!matched.length) { addNotification('No matched vehicles to update', 'error'); return; }
    setApplying(true);
    setProgress({ done: 0, total: matched.length, current: '' });

    let fileBase64 = null;
    if (fileDocs && pdfFile) {
      try { fileBase64 = await fileToBase64(pdfFile); } catch { /* filing is best-effort */ }
    }
    const policyLabel = `Fleet policy${policy?.policyNumber ? ` ${policy.policyNumber}` : ''} — exp ${fmtDate(expiry)}`;

    let updated = 0, failed = 0, filed = 0;
    for (let i = 0; i < matched.length; i++) {
      const m = matched[i];
      setProgress({ done: i, total: matched.length, current: m.vehicle.rego });
      try {
        const res = await updateVehicleInsurance(m.vehicle, expiry);
        if (res?.success) {
          updated++;
          setVehicles(vs => vs.map(v => v.rego === m.vehicle.rego ? { ...v, insurance_exp: expiry } : v));
        } else {
          failed++;
        }
      } catch { failed++; }

      // Best-effort: file the same policy PDF under this vehicle's private docs.
      if (fileBase64) {
        try {
          const d = await fetch(VEHICLE_ADMIN_URL, {
            method: 'POST',
            body: JSON.stringify({
              action: 'saveVehicleDocument',
              rego: (m.vehicle.rego || '').toUpperCase(),
              category: 'Insurance',
              label: policyLabel,
              filename: pdfFile.name,
              contentType: pdfFile.type || 'application/pdf',
              fileBase64,
              extracted: { insuranceExpiry: expiry, summary: policy?.summary || '' },
              uploadedBy: currentUser?.login || '',
            }),
          }).then(r => r.json());
          if (d?.success) filed++;
        } catch { /* non-fatal */ }
      }
      await new Promise(r => setTimeout(r, 120));
    }

    setProgress(null);
    setApplying(false);
    setDoneSummary({ updated, failed, filed });
    addNotification(
      `Insurance updated on ${updated} vehicle${updated !== 1 ? 's' : ''}${failed ? ` · ${failed} failed` : ''}${fileBase64 ? ` · ${filed} filed` : ''}`,
      failed ? 'error' : 'success',
    );
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1000,
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      paddingTop: 40, overflowY: 'auto',
    }}>
      <div style={{
        width: 760, maxHeight: '88vh', background: 'var(--hp-bg)',
        borderRadius: 14, border: '1px solid var(--hp-border)',
        display: 'flex', flexDirection: 'column', margin: '0 16px',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '22px 26px 18px', borderBottom: '1px solid var(--hp-border)', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--hp-gold)', marginBottom: 5 }}>Fleet · Insurance</div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.2rem', fontWeight: 700 }}>Fleet Insurance Policy</h3>
            <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--hp-muted)' }}>
              Upload the policy once — it reads the expiry date and the schedule of regos, then marks every matched vehicle insured.
            </p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 26px' }}>

          {/* 1. Dropzone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? 'var(--hp-gold)' : pdfFile ? 'var(--hp-green)' : 'var(--hp-gold)66'}`,
              borderRadius: 10, padding: '18px 14px', textAlign: 'center', cursor: 'pointer',
              background: dragOver ? 'var(--hp-surface)' : 'transparent', transition: 'border-color .15s, background .15s',
            }}
          >
            {pdfFile ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.82rem', color: 'var(--hp-text)' }}>
                {extracting
                  ? <Loader size={14} color="var(--hp-gold)" style={{ animation: 'spin 1s linear infinite' }} />
                  : <Paperclip size={14} color="var(--hp-green)" />}
                <span style={{ fontWeight: 600 }}>{pdfFile.name}</span>
                <span style={{ color: 'var(--hp-muted)', fontSize: '0.72rem' }}>({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); clearFile(); }} title="Remove"><X size={12} /></button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
                  <FileText size={16} color="var(--hp-gold)" />
                  <span><strong style={{ color: 'var(--hp-text)' }}>Drop the policy PDF here</strong> — or click to browse</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)', marginTop: 5 }}>
                  up to {MAX_DOC_MB} MB · reads the expiry + the schedule of regos
                </div>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={handleFilePick} />

          <label className="btn btn-secondary btn-sm" style={{ marginTop: 8, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
            <Camera size={14} /> {scanPages.length ? `Add another page (page ${scanPages.length + 1})` : 'Scan with camera'}
            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleScanPick} />
          </label>
          {scanPages.length > 0 && (
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', marginTop: 6, textAlign: 'center' }}>
              {scanPages.length} page{scanPages.length > 1 ? 's' : ''} scanned — combined into one PDF.
            </div>
          )}

          {extracting && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: '0.8rem', color: 'var(--hp-gold)' }}>
              <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> Reading the policy & matching the schedule…
            </div>
          )}

          {/* 2. Review */}
          {policy && !extracting && (
            <>
              <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 20 }}>
                <div>
                  <label style={labelStyle}><Calendar size={11} style={{ verticalAlign: -1 }} /> Policy expiry (applied to all matched)</label>
                  <input type="date" className="input" style={inputStyle} value={expiry} onChange={e => setExpiry(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Insurer / policy</label>
                  <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)', paddingTop: 6 }}>
                    {policy.insurer || '—'}{policy.policyNumber ? ` · ${policy.policyNumber}` : ''}
                  </div>
                </div>
              </div>

              {/* matched summary */}
              <div style={{ display: 'flex', gap: 16, marginTop: 18, marginBottom: 8, fontSize: '0.8rem' }}>
                <span style={{ color: 'var(--hp-green)', fontWeight: 600 }}>
                  <CheckCircle size={13} style={{ verticalAlign: -2 }} /> {matched.length} matched in fleet
                </span>
                {unmatched.length > 0 && (
                  <span style={{ color: 'var(--hp-amber)', fontWeight: 600 }}>
                    <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> {unmatched.length} need attention
                  </span>
                )}
              </div>

              <div style={{ border: '1px solid var(--hp-border)', borderRadius: 10, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--hp-border)' }}>
                      {['Policy rego', 'Vehicle', 'Current', 'After'].map((h, i) => (
                        <th key={i} style={{ textAlign: 'left', padding: '9px 14px', color: 'var(--hp-muted)', fontWeight: 600, fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((m, i) => {
                      const suggestion = m.vehicle ? null : suggestFor(m.canon);
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--hp-border)22' }}>
                          <td style={{ padding: '9px 14px', fontFamily: 'var(--font-mono)', fontWeight: 800, color: 'var(--hp-gold)', fontSize: '0.85rem', verticalAlign: 'top' }}>{m.rego}</td>
                          <td style={{ padding: '9px 14px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
                            {m.vehicle ? (
                              <span>
                                {m.remapped && <span style={{ color: 'var(--hp-amber)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>→ {m.vehicle.rego} </span>}
                                {vehLabel(m.vehicle) || m.description || '—'}
                                {m.remapped && (
                                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6, padding: '0 4px' }} title="Undo remap" onClick={() => setRemapFor(m.canon, '')}><X size={11} /></button>
                                )}
                              </span>
                            ) : (
                              <div>
                                <div style={{ color: 'var(--hp-amber)', marginBottom: 5 }}>not in fleet{m.description ? ` — ${m.description}` : ''}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  {suggestion && (
                                    <button className="btn btn-secondary btn-sm" style={{ padding: '2px 8px' }} onClick={() => setRemapFor(m.canon, canonRego(suggestion.rego))}>
                                      <Wand2 size={11} /> {suggestion.rego}?
                                    </button>
                                  )}
                                  <select
                                    className="input"
                                    style={{ ...inputStyle, fontSize: '0.74rem', padding: '2px 6px', maxWidth: 220 }}
                                    value={remap[m.canon] || ''}
                                    onChange={e => setRemapFor(m.canon, e.target.value)}
                                  >
                                    <option value="">map to fleet vehicle…</option>
                                    {fleetSorted.map(v => (
                                      <option key={v.rego} value={canonRego(v.rego)}>{v.rego} — {vehLabel(v) || '—'}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '9px 14px', fontSize: '0.8rem', color: 'var(--hp-muted)', verticalAlign: 'top' }}>
                            {m.vehicle ? fmtDate(m.vehicle.insurance_exp) : '—'}
                          </td>
                          <td style={{ padding: '9px 14px', fontSize: '0.8rem', color: m.vehicle ? 'var(--hp-green)' : 'var(--hp-muted)', fontWeight: m.vehicle ? 600 : 400, verticalAlign: 'top' }}>
                            {m.vehicle ? (expiry ? fmtDate(expiry) : '—') : 'skipped'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* FYI — company vehicles not on this policy */}
              {notOnPolicy.length > 0 && (
                <div style={{ marginTop: 14, border: '1px solid var(--hp-amber)44', borderRadius: 10, overflow: 'hidden' }}>
                  <button onClick={() => setShowGaps(s => !s)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
                    <AlertTriangle size={14} color="var(--hp-amber)" />
                    <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{notOnPolicy.length} company vehicle{notOnPolicy.length !== 1 ? 's' : ''} in the fleet not on this policy</span>
                    <span style={{ marginLeft: 'auto', color: 'var(--hp-muted)' }}>{showGaps ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
                  </button>
                  {showGaps && (
                    <div style={{ borderTop: '1px solid var(--hp-border)22' }}>
                      {notOnPolicy.map(v => (
                        <div key={v.rego} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', borderBottom: '1px solid var(--hp-border)11', fontSize: '0.8rem' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: 'var(--hp-gold)', minWidth: 72 }}>{v.rego}</span>
                          <span style={{ color: 'var(--hp-dim)', flex: 1 }}>{vehLabel(v) || '—'}</span>
                          <span style={{ color: 'var(--hp-muted)', fontSize: '0.72rem' }}>{v.owner || '—'}</span>
                          <span style={{ color: v.insurance_exp ? 'var(--hp-muted)' : 'var(--hp-red)', fontSize: '0.72rem' }}>
                            ins: {v.insurance_exp ? fmtDate(v.insurance_exp) : 'none'}
                          </span>
                        </div>
                      ))}
                      <div style={{ padding: '8px 14px', fontSize: '0.7rem', color: 'var(--hp-muted)' }}>
                        Owner-driver vehicles are hidden (they self-insure). Review these — they may be insured elsewhere, sold, or a gap.
                      </div>
                    </div>
                  )}
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: '0.82rem', color: 'var(--hp-text)', cursor: 'pointer' }}>
                <input type="checkbox" checked={fileDocs} onChange={e => setFileDocs(e.target.checked)} />
                Also file this policy PDF under each matched vehicle’s private documents (Insurance)
              </label>

              {progress && (
                <div style={{ marginTop: 14, fontSize: '0.8rem', color: 'var(--hp-muted)' }}>
                  <Loader size={12} style={{ animation: 'spin 1s linear infinite', verticalAlign: -2 }} /> Updating {progress.current}… ({progress.done}/{progress.total})
                </div>
              )}

              {doneSummary && (
                <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--hp-surface)', borderRadius: 10, fontSize: '0.82rem', color: 'var(--hp-text)' }}>
                  <CheckCircle size={14} color="var(--hp-green)" style={{ verticalAlign: -2 }} /> Updated {doneSummary.updated} vehicle{doneSummary.updated !== 1 ? 's' : ''}
                  {doneSummary.failed ? <span style={{ color: 'var(--hp-red)' }}> · {doneSummary.failed} failed</span> : null}
                  {fileDocs ? ` · ${doneSummary.filed} policy file${doneSummary.filed !== 1 ? 's' : ''} filed` : ''}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 26px', borderTop: '1px solid var(--hp-border)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Lock size={12} /> Writes Insurance Expiry to the fleet · admin-only
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={onClose}>{doneSummary ? 'Close' : 'Cancel'}</button>
            <button className="btn btn-primary" onClick={handleApply} disabled={applying || !policy || !expiry || !matched.length}>
              {applying
                ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Applying…</>
                : <><ShieldCheck size={14} /> Mark {matched.length || ''} insured</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
